import { colors, formatters, sleep } from "@fraction/shared";
import { type MotionProps, motion, useCycle } from "framer-motion";
import type Inputmask from "inputmask";
import * as React from "react";
import {
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { type NumberFormatValues, NumericFormat } from "react-number-format";

// Relaxed cast: NumericFormat's prop union × framer-motion's MotionProps overflow TS's union-size
// limit (TS2590) — alias the component as a permissive FC so the JSX below typechecks.
const NumericFormatRelaxed = NumericFormat as React.FC<Record<string, unknown>>;

import AnimatedLabel from "src/components/AnimatedLabel";
import useInputMask from "src/hooks/useInputMask";
import useLabel from "src/hooks/useLabel";
import useMergedRef from "src/hooks/useMergedRef";
import usePrevious from "src/hooks/usePrevious";
import { editInputValueAndForceOnChangeEvent } from "src/utilities/react";
import { cn } from "src/utilities/shadcnUtils";

import XButton from "../XButton";

export type TextInputEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

export enum Variant {
  STANDARD = "standard",
  DISABLED_OUTLINED = "disabled-outlined",
  V2 = "v2",
  ROUNDED = "rounded",
}

export const ROUNDED_RADIUS = 22;
export const V2_RADIUS = 6;

export interface TextInputProps
  extends Omit<
    React.DetailedHTMLProps<
      InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
      HTMLInputElement | HTMLTextAreaElement
    > &
      MotionProps,
    "onChange" | "style" | "onAnimationStart" | "onDragStart" | "onDragEnd"
  > {
  label?: string;
  error?: string;
  disabled?: boolean;
  value?: string;
  placeholder?: string;
  type?: string;
  name?: string;
  onChange?: (val: string, event: TextInputEvent) => void;
  mask?: string | Inputmask.Options["mask"];
  maskOptions?: Inputmask.Options & { decimalScale?: number };
  className?: string;
  inputClassName?: string;
  borderRadius?: number;
  multiline?: boolean;
  divRef?: Ref<HTMLDivElement>;
  adornment?: ReactNode;
  variant?: Variant | string;
  clearable?: boolean;
  errorSpacing?: boolean;
  containerClassName?: string;
  noCss?: boolean;
  currency?: boolean;
  percentage?: boolean;
  integer?: boolean;
  cents?: boolean;
  phone?: boolean;
}

// Base input styles (preserves the prior INPUT_STYLES via Tailwind). Exported because
// other components import it.
export const INPUT_BASE_CLASSES = [
  "bg-transparent py-[13px] pl-6 pr-[18px]",
  "border border-solid",
  "min-w-[340px] w-full max-w-full max-[500px]:w-full max-[500px]:min-w-[unset]",
  "box-border",
  "font-['Hanken_Grotesk',sans-serif] tracking-[0.02em]",
  "text-foreground m-0 text-base font-normal",
  "border-gray-400 caret-green-500",
  "hover:placeholder:text-gray-600",
  "placeholder:text-gray-500",
  "focus:outline-none focus:border-green-500",
  "disabled:opacity-80 disabled:text-gray-400",
].join(" ");

export const TEXT_INPUT_STYLES = {
  input: INPUT_BASE_CLASSES,
  v2: "rounded-md",
  rounded: `rounded-[${ROUNDED_RADIUS}px]`,
  withLabel: "border-t-0",
  error: "border-red-500 focus:border-red-500",
  errorText:
    "font-['Hanken_Grotesk',sans-serif] tracking-[0.02em] m-0 text-red-500 text-xs mt-[3px] max-w-fit",
  spacer: "h-[18px]",
  multiline: "h-[120px] max-h-full leading-[160%] resize-none",
  container: "relative min-w-fit",
  adornment: "absolute top-0 right-0 h-[calc(100%-18px)] flex items-center justify-end w-fit",
  disabledOutlined: "disabled:opacity-100 disabled:text-gray-500 disabled:bg-gray-200",
  clearable: "absolute top-0 right-0 h-full flex justify-center items-center",
  clearableSpacing: "h-[calc(100%-18px)]",
  clearIcon: "bg-transparent",
};

const styles = TEXT_INPUT_STYLES;

const TextInput = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>(
  (
    {
      placeholder,
      label,
      error,
      errorSpacing = true,
      disabled,
      onChange,
      phone,
      mask,
      maskOptions,
      value,
      defaultValue,
      className,
      inputClassName,
      multiline,
      divRef,
      max,
      min,
      adornment,
      variant = Variant.V2,
      borderRadius = variant === Variant.V2 ? V2_RADIUS : variant === Variant.ROUNDED ? ROUNDED_RADIUS : 10,
      clearable,
      containerClassName,
      currency,
      percentage,
      integer,
      cents,
      noCss,
      ...props
    }: TextInputProps,
    forwardedRef
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const isNumericMask = currency || cents || percentage || integer;
    const usesMask = mask || phone;
    const otherBorderRadii =
      variant === Variant.V2 ? V2_RADIUS : variant === Variant.ROUNDED ? ROUNDED_RADIUS : 0;

    const allowMinus = min === undefined || formatters.number.getNumberFromString(min) < 0;

    // For phone/pattern masks, keep existing inputmask options
    const options = useMemo(
      () => ({
        placeholder: " ",
        clearMaskOnLostFocus: true,
        jitMasking: true,
        autoUnmask: true,
        showMaskOnHover: false,
        max,
        min,
        allowMinus,
        ...maskOptions,
      }),
      [max, min, maskOptions, allowMinus]
    );

    const handleChange = useCallback(
      (event: TextInputEvent) => {
        onChange?.(event?.target?.value, event);
      },
      [onChange]
    );

    const handleNumericValueChange = useCallback(
      (values: NumberFormatValues, sourceInfo: { event?: React.SyntheticEvent }) => {
        if (sourceInfo.event) {
          const syntheticEvent = sourceInfo.event as TextInputEvent;
          onChange?.(values.value, syntheticEvent);
        }
      },
      [onChange]
    );

    const merged = useMergedRef(
      useInputMask({
        mask: phone ? PHONE_MASK : mask,
        options,
        // @ts-expect-error
        onChange: handleChange,
      }),
      forwardedRef
    );

    const innerMerged = useMergedRef(merged, innerRef);
    const forwardedMerged = useMergedRef(forwardedRef, innerRef);

    const ref = usesMask ? innerMerged : forwardedMerged;
    const numericRef = useMergedRef(forwardedRef, innerRef);

    const [inputProps, labelProps] = useLabel({
      inputRef: innerRef,
      label,
      placeholder,
      disabled: variant !== Variant.DISABLED_OUTLINED && disabled,
      error,
      defaultValue,
      value,
      ...props,
    });

    const prevError = usePrevious(error);

    const handleClear = useCallback(() => {
      editInputValueAndForceOnChangeEvent("", innerRef);
    }, [innerRef]);

    const [x, cycleX] = useCycle(0, 5, -5, 5, 0);

    useEffect(() => {
      const jiggle = async () => {
        for (let i = 0; i < 5; i++) {
          await sleep(80);
          cycleX();
        }
      };

      if (error && error !== prevError) {
        jiggle();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, prevError]);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const Component = multiline ? motion.textarea : motion.input;

    const inputClassNameFinal = noCss
      ? inputClassName
      : cn(
          styles.input,
          error && styles.error,
          multiline && styles.multiline,
          label && styles.withLabel,
          variant === Variant.DISABLED_OUTLINED && styles.disabledOutlined,
          variant === Variant.V2 && styles.v2,
          variant === Variant.ROUNDED && styles.rounded,
          inputClassName
        );

    // labelProps.borderColor is a dynamic color value — pass through inline style
    const inlineBorderStyle =
      !noCss && labelProps.borderColor ? { borderColor: labelProps.borderColor } : undefined;

    return (
      <div ref={divRef} className={cn(noCss ? undefined : styles.container, containerClassName)}>
        {isNumericMask ? (
          // NumericFormat × framer-motion's MotionProps blow past TS's union-size limit;
          // cast NumericFormat to a relaxed FC so motion props + customInput still typecheck.
          <NumericFormatRelaxed
            customInput={motion.input}
            // Pass through safe HTML + motion attributes explicitly
            id={props.id}
            name={props.name}
            required={props.required}
            autoComplete={props.autoComplete}
            autoFocus={props.autoFocus}
            tabIndex={props.tabIndex}
            onKeyDown={props.onKeyDown}
            aria-label={props["aria-label"]}
            aria-describedby={props["aria-describedby"]}
            value={value ?? undefined}
            defaultValue={
              defaultValue === undefined || defaultValue === null
                ? undefined
                : typeof defaultValue === "number"
                  ? defaultValue
                  : String(defaultValue)
            }
            initial={noCss ? undefined : { borderBottomLeftRadius: "0px" }}
            animate={noCss ? undefined : { borderBottomLeftRadius: `${borderRadius}px` }}
            className={cn(inputClassNameFinal, className)}
            style={inlineBorderStyle}
            disabled={disabled}
            {...inputProps}
            getInputRef={numericRef}
            thousandSeparator=","
            prefix={currency || cents ? "$ " : undefined}
            suffix={percentage ? "%" : undefined}
            decimalScale={maskOptions?.decimalScale ?? (cents ? 2 : percentage ? 2 : 0)}
            fixedDecimalScale={!!cents}
            allowNegative={allowMinus}
            onValueChange={handleNumericValueChange}
          />
        ) : (
          <Component
            {...props}
            // @ts-expect-error
            onFocus={props.onFocus}
            value={value}
            id={props.id}
            required={props.required}
            defaultValue={defaultValue}
            initial={noCss ? undefined : { borderBottomLeftRadius: "0px" }}
            animate={noCss ? undefined : { borderBottomLeftRadius: `${borderRadius}px` }}
            onChange={handleChange}
            className={cn(inputClassNameFinal, className)}
            style={inlineBorderStyle}
            disabled={disabled}
            max={max}
            min={min}
            step={props.step}
            {...inputProps}
            ref={ref}
          />
        )}
        {clearable && (
          <div className={cn(styles.clearable, errorSpacing && styles.clearableSpacing)}>
            <XButton onClick={handleClear} color={colors.palette.GREY_400} className={styles.clearIcon} />
          </div>
        )}
        {adornment && <div className={styles.adornment}>{adornment}</div>}
        {label && (
          <AnimatedLabel {...labelProps} borderRadius={otherBorderRadii}>
            {label}
          </AnimatedLabel>
        )}
        {error && (
          <motion.p animate={{ x }} className={styles.errorText}>
            {error}
          </motion.p>
        )}
        {!error && errorSpacing && <div className={styles.spacer} />}
      </div>
    );
  }
);

export const CURRENCY_MASK = {
  rightAlign: false,
  alias: "currency",
  prefix: "$ ",
  jitMasking: false,
  digits: 0,
};

export const CENTS_CURRENCY_MASK = {
  rightAlign: false,
  alias: "currency",
  prefix: "$ ",
  jitMasking: false,
  digits: 2,
};

export const PERCENTAGE_MASK = {
  alias: "percentage",
  rightAlign: false,
  jitMasking: true,
  suffix: "%",
  digits: 2,
};

export const INTEGER_MASK = {
  alias: "integer",
  digits: 0,
  rightAlign: false,
  jitMasking: true,
};

// Keep the domestic mask first so 10-digit values do not gain a country code.
export const PHONE_MASK = ["(999) 999-9999", "+1 (999) 999-9999"];

export default React.memo(
  TextInput,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps.maskOptions || {}) === JSON.stringify(nextProps.maskOptions || {}) &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.value === nextProps.value &&
    prevProps.defaultValue === nextProps.defaultValue &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.type === nextProps.type &&
    prevProps.step === nextProps.step &&
    prevProps.autoComplete === nextProps.autoComplete &&
    prevProps.name === nextProps.name &&
    prevProps.mask === nextProps.mask &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.error === nextProps.error &&
    prevProps.className === nextProps.className &&
    prevProps.inputClassName === nextProps.inputClassName &&
    prevProps.multiline === nextProps.multiline &&
    prevProps.adornment === nextProps.adornment &&
    prevProps.containerClassName === nextProps.containerClassName &&
    prevProps.onSubmit === nextProps.onSubmit &&
    prevProps.noCss === nextProps.noCss &&
    prevProps.currency === nextProps.currency &&
    prevProps.percentage === nextProps.percentage &&
    prevProps.integer === nextProps.integer &&
    prevProps.cents === nextProps.cents &&
    prevProps.phone === nextProps.phone &&
    prevProps.clearable === nextProps.clearable
);
